import React, { useEffect, useState } from 'react'
import {
  CalendarOutlined,
  CheckCircleOutlined,
  FileExclamationOutlined,
  FireOutlined,
  LineChartOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { Skeleton } from 'antd'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { fetchDashboardStats } from '~actions'
import { UpcomingEvents, EventSales } from '~components'
import { PageLayout } from '~stories'
import styles from './Dashboard.module.css'

const Dashboard = ({
  dashboardStats,
  eventPublishStatus,
  fetchDashboardStats,
  unPublishedEvents,
  upcomingEvents
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState()
  const { userDetails } = JSON.parse(localStorage.getItem('session'))
  const { publish: publishedEventsCount = 0, unpublish: unPublishedEventsCount = 0 } = eventPublishStatus
  const totalEventsCount = publishedEventsCount + unPublishedEventsCount

  const [stats] = dashboardStats
  const { events: { most_available: mostAvailable = [], top_selling: topSelling = [] } } = stats || { events: {} }

  useEffect(() => {
    setIsDataLoaded(true)
    fetchDashboardStats()
  }, [fetchDashboardStats])

  useEffect(() => {
    if (dashboardStats && dashboardStats.length > 0) {
      setIsDataLoaded(false)
    }
  }, [dashboardStats])

  const { userName } = userDetails

  return (
    <PageLayout>
      <div className={styles.dashboardPage}>
        {/* ── Welcome header ──────────────────────────────────── */}
        <div className={styles.headerSection}>
          <h2 className={styles.header}>Welcome, {userName}!</h2>
        </div>

        {/* ── Stat cards ──────────────────────────────────────── */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <span className={`${styles.statIcon} ${styles.statIconTotal}`}>
                <UnorderedListOutlined />
              </span>
              <span className={styles.statLabel}>Total Events</span>
            </div>
            <Skeleton active loading={isDataLoaded} paragraph={false}>
              <p className={styles.statValue}>{totalEventsCount}</p>
            </Skeleton>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <span className={`${styles.statIcon} ${styles.statIconPublished}`}>
                <CheckCircleOutlined />
              </span>
              <span className={styles.statLabel}>Published Events</span>
            </div>
            <Skeleton active loading={isDataLoaded} paragraph={false}>
              <p className={styles.statValue}>{publishedEventsCount}</p>
            </Skeleton>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <span className={`${styles.statIcon} ${styles.statIconUnpublished}`}>
                <FileExclamationOutlined />
              </span>
              <span className={styles.statLabel}>Unpublished Events</span>
            </div>
            <Skeleton active loading={isDataLoaded} paragraph={false}>
              <p className={styles.statValue}>{unPublishedEventsCount}</p>
            </Skeleton>
          </div>
        </div>

        {/* ── Unpublished / Upcoming ──────────────────────────── */}
        <div className={styles.contentRow}>
          <div className={styles.contentCard}>
            <div className={styles.contentCardHead}>
              <h3 className={styles.contentCardTitle}>
                <FileExclamationOutlined style={{ marginRight: 8, color: 'var(--color-warning, #faad14)' }} />
                Unpublished Events
              </h3>
            </div>
            <div className={styles.contentCardBody}>
              <Skeleton active avatar loading={isDataLoaded}>
                <UpcomingEvents listItems={unPublishedEvents} />
              </Skeleton>
            </div>
          </div>

          <div className={styles.contentCard}>
            <div className={styles.contentCardHead}>
              <h3 className={styles.contentCardTitle}>
                <CalendarOutlined style={{ marginRight: 8, color: 'var(--color-primary, #1677ff)' }} />
                Upcoming Shows
              </h3>
            </div>
            <div className={styles.contentCardBody}>
              <Skeleton active avatar loading={isDataLoaded}>
                <UpcomingEvents listItems={upcomingEvents} />
              </Skeleton>
            </div>
          </div>
        </div>

        {/* ── Top Selling / Least Sold ────────────────────────── */}
        <div className={styles.contentRow}>
          <div className={styles.contentCard}>
            <div className={styles.contentCardHead}>
              <h3 className={styles.contentCardTitle}>
                <FireOutlined style={{ marginRight: 8, color: 'var(--color-error, #ff4d4f)' }} />
                Most Sold
              </h3>
            </div>
            <div className={styles.contentCardBody}>
              <Skeleton active avatar loading={isDataLoaded}>
                <EventSales listItems={topSelling} />
              </Skeleton>
            </div>
          </div>

          <div className={styles.contentCard}>
            <div className={styles.contentCardHead}>
              <h3 className={styles.contentCardTitle}>
                <LineChartOutlined style={{ marginRight: 8, color: 'var(--color-info, #1677ff)' }} />
                Least Sold
              </h3>
            </div>
            <div className={styles.contentCardBody}>
              <Skeleton active avatar loading={isDataLoaded}>
                <EventSales listItems={mostAvailable} />
              </Skeleton>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

Dashboard.propTypes = {
  dashboardStats: PropTypes.array,
  eventPublishStatus: PropTypes.object,
  fetchDashboardStats: PropTypes.func.isRequired,
  unPublishedEvents: PropTypes.array,
  upcomingEvents: PropTypes.array
}

Dashboard.defaultProps = {
  dashboardStats: [],
  eventPublishStatus: {},
  featureFlagMenus: [],
  unPublishedEvents: [],
  upcomingEvents: []
}

const mapStateToProps = ({
  dashboard: {
    dashboardStats,
    eventPublishStatus,
    featureFlagMenus,
    unPublishedEvents,
    upcomingEvents
  }
}) => ({
  dashboardStats,
  eventPublishStatus,
  featureFlagMenus,
  unPublishedEvents,
  upcomingEvents
})

const mapDispatchToProps = {
  fetchDashboardStats
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard)
